<template>
  <button
    v-if="hasFooter"
    type="button"
    :class="['automations-footer-bar', { 'automations-footer-bar--suggestion': isMissed }]"
    data-automations-footer
    aria-label="View automations details"
    @click.stop="handleClick"
  >
    <span
      class="automations-footer-icon-badge"
      :class="{
        'automations-footer-icon-badge--green': footerState?.state === 'covered',
        'automations-footer-icon-badge--yellow': footerState?.state === 'missed',
        'automations-footer-icon-badge--grey': footerState?.state === 'noCoverage'
      }"
    >
      <span class="material-icons-outlined">bolt</span>
    </span>

    <span class="automations-footer-text">{{ footerState?.text || '' }}</span>
    <span class="material-icons-outlined automations-footer-chevron">chevron_right</span>
  </button>

  <Teleport to="body">
    <Transition name="slide-panel">
      <div
        v-if="overlayOpen"
        class="panel-overlay"
        @click.self="overlayOpen = false"
      >
        <div class="panel-drawer">
          <WidgetDetailPanel
            :title="title"
            :widget-type="widgetType"
            @close="overlayOpen = false"
          />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, inject, ref } from 'vue'
import { pushAdminSidePanel, isInOptixIframe } from '../composables/useOptixPanel.js'
import { useBestPracticesData, getFooterStateForWidget } from '../composables/useBestPracticesData.js'
import { WIDGET_TO_BEST_PRACTICE_TYPES } from '../utils/widgetBestPractices.js'
import WidgetDetailPanel from './WidgetDetailPanel.vue'

const props = defineProps({
  /** Widget type for this widget (e.g. 'upcoming-tours') */
  widgetType: {
    type: String,
    default: null
  },
  /** Widget title for panel */
  title: {
    type: String,
    default: ''
  },
  editMode: {
    type: Boolean,
    default: false
  }
})

const { bestPractices } = useBestPracticesData()
const trackPosthogEvent = inject('trackPosthogEvent', () => {})

const hasLinkedTypes = computed(() =>
  props.widgetType && (WIDGET_TO_BEST_PRACTICE_TYPES[props.widgetType]?.length > 0)
)

const footerState = computed(() => {
  // Trigger reactivity on bestPractices changes
  if (!bestPractices.value) return null
  return props.widgetType ? getFooterStateForWidget(props.widgetType) : null
})

const hasFooter = computed(() =>
  !props.editMode && hasLinkedTypes.value && footerState.value != null
)

const isMissed = computed(() => footerState.value?.state === 'missed')

const overlayOpen = ref(false)

const handleClick = () => {
  if (!props.widgetType) return
  trackPosthogEvent('Dashboard: Automation footer clicked', {
    widgetType: props.widgetType,
    footerState: footerState.value?.state || 'unknown',
    footerText: footerState.value?.text || ''
  })
  if (isInOptixIframe()) {
    pushAdminSidePanel('widget-detail', { title: props.title, widgetType: props.widgetType })
  } else {
    overlayOpen.value = true
  }
}
</script>

<style scoped>
.automations-footer-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  width: calc(100% + 40px);
  margin: 0 -20px -10px -20px;
  min-height: 48px;
  padding: 8px 20px;
  border: none;
  border-radius: 0 0 12px 12px;
  background: rgba(255, 255, 255, 1);
  color: rgba(0, 0, 0, 0.6);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  text-align: left;
  position: relative;
  z-index: 1;
}

.automations-footer-bar::before {
  content: '';
  position: absolute;
  top: 0;
  left: 20px;
  right: 20px;
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
  transition: left 0.2s ease, right 0.2s ease;
}

.automations-footer-bar:hover::before {
  left: 0;
  right: 0;
}

.automations-footer-bar:hover {
  background: #e9ebee;
}

.automations-footer-bar--suggestion {
  background: white;
  color: var(--color-yellow-text);
}

.automations-footer-bar--suggestion:hover {
  background: #fffbeb;
}

.automations-footer-icon-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  flex-shrink: 0;
}

.automations-footer-icon-badge .material-icons-outlined {
  font-size: 14px;
}

.automations-footer-icon-badge--green {
  background: var(--color-bg-green);
}

.automations-footer-icon-badge--green .material-icons-outlined {
  color: #1C3829;
}

.automations-footer-icon-badge--grey {
  background: rgba(241, 240, 239, 1);
}

.automations-footer-icon-badge--grey .material-icons-outlined {
  color: #6b7280;
}

.automations-footer-icon-badge--yellow {
  background: var(--color-bg-yellow);
}

.automations-footer-icon-badge--yellow .material-icons-outlined {
  color: var(--color-yellow-text);
}

.automations-footer-bar--suggestion .automations-footer-text {
  color: rgba(0, 0, 0, 0.68);
}

.automations-footer-text {
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: rgba(0, 0, 0, 0.68);
  padding-top: 2px;
  line-height: 1.4;
}

.automations-footer-chevron {
  flex-shrink: 0;
  font-size: 18px;
  opacity: 0;
  transform: translateX(-4px);
  transition: opacity 0.2s ease, transform 0.2s ease;
  color: inherit;
}

.automations-footer-bar:hover .automations-footer-chevron {
  opacity: 1;
  transform: translateX(0);
}

/* Overlay panel for non-iframe mode */
.panel-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 9999;
  display: flex;
  justify-content: flex-end;
}

.panel-drawer {
  width: 580px;
  max-width: 100%;
  height: 100%;
  background: white;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
}

.slide-panel-enter-active,
.slide-panel-leave-active {
  transition: opacity 0.25s ease;
}

.slide-panel-enter-active .panel-drawer,
.slide-panel-leave-active .panel-drawer {
  transition: transform 0.25s ease;
}

.slide-panel-enter-from {
  opacity: 0;
}

.slide-panel-enter-from .panel-drawer {
  transform: translateX(100%);
}

.slide-panel-leave-to {
  opacity: 0;
}

.slide-panel-leave-to .panel-drawer {
  transform: translateX(100%);
}
</style>
